import React, { useEffect, useState } from 'react'
import InventoryCreateForm from '../components/InventoryCreateForm';
import apiCall from '../helper/apiCall';
import Modal from '../components/Modal';

const Inventory = () => {
   const [inventories, setInventories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [inventoryToEdit, setInventoryToEdit] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Function to show a temporary message
  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // Fetch all data from the backend
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const inventoryResult = await apiCall('getInventories');
      if (inventoryResult.success && inventoryResult.data) {
        setInventories(inventoryResult.data);
      } else {
        setInventories([]);
        showMessage("Failed to fetch inventories.", 'error');
      }

      const statesResult = await apiCall('getStates');
      if (statesResult.success && statesResult.data) {
        setStates(statesResult.data);
      }
      
      const countriesResult = await apiCall('getCountries');
      if (countriesResult.success && countriesResult.data) {
        setCountries(countriesResult.data);
      }

    } catch (error) {
      console.error("Failed to fetch data:", error);
      showMessage("Error fetching data.", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddInventory = () => {
    setModalType('add');
    setIsModalOpen(true);
    setInventoryToEdit(null);
  };

  const handleEditInventory = (inventory) => {
    setModalType('edit');
    setIsModalOpen(true);
    setInventoryToEdit(inventory);
  };

  const handleSaveInventory = async (newInventoryData) => {
    let result;
    if (modalType === 'add') {
      result = await apiCall('createInventory', newInventoryData);
    } else {
      result = await apiCall('updateInventory', newInventoryData);
    }
    
    if (result.success) {
      fetchData(); 
      showMessage(`Inventory ${modalType === 'add' ? 'created' : 'updated'} successfully!`);
    } else {
      showMessage(`Failed to ${modalType === 'add' ? 'create' : 'update'} inventory.`, 'error');
    }
    setIsModalOpen(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setInventoryToEdit(null);
  };
  
  return (
    <div className="p-4 sm:p-8 font-sans bg-gray-100 min-h-screen">
      <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4 sm:mb-6">Inventory Management</h1>
      
      {message && (
        <div className={`p-3 mb-4 rounded-lg text-sm text-center ${message.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
          {message.text}
        </div>
      )}

      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <span className="text-lg font-semibold text-gray-700">All Inventories</span>
        <button
          onClick={handleAddInventory}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-md hover:bg-indigo-700 transition-colors"
        >
          Add New Inventory
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading inventories...</div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Inventory Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {inventories.length > 0 ? (
                  inventories.map(inventory => (
                    <tr key={inventory.inventory_id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{inventory.inventory_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {inventory.address.address_line_1}, {inventory.address.city}, {inventory.address.pincode}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${inventory.status === 'A' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {inventory.status === 'A' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEditInventory(inventory)}
                          className="text-indigo-600 hover:text-indigo-900 transition-colors"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500">No inventories found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={modalType === 'add' ? 'Add New Inventory' : 'Edit Inventory'}
      >
        <InventoryCreateForm
          initialData={inventoryToEdit}
          onSave={handleSaveInventory}
          onClose={handleCloseModal}
          countries={countries}
          states={states}
        />
      </Modal>
    </div>
  );
};


export default Inventory